/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { ActionButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { IStackItemStyles, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { Table } from 'src/core/components/Table';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useSelection } from 'src/core/hooks/useSelection';
import { logDTButtonClick, logDTLinkClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { AzureContainerBrowsingModal } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/AzureContainerBrowsingModal';
import { TargetFile, TranslatorLanguage, useTranslatorCapabilities } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { LanguageInfo, TranslationCreationSteps, TranslationInfoDto } from 'src/translation/components/TranslationCreationPage';
import { TranslationToolFooter } from 'src/translation/components/TranslationToolFooter';

export type TranslationAdditionalOptionsPageProps = {
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    onNext: (customTranslatorModelId: string, glossaryFiles: TargetFile[]) => void;
    translationConfigsDto: TranslationInfoDto;
    activeStep: TranslationCreationSteps;
    isReturningUser: boolean;
    storageAccounts: AzureStorageAccountStore[];
    selectedStorageAccount: string;
    resourceGroupName: string;
    selectedTargetLanguages: LanguageInfo[];
    createdStorageContainerName: string;
    supportedLanguages: TranslatorLanguage[];
};

const messages = defineMessages({
    pageTitle: { id: 'translation.translationAdditionalOptionsPage.pageTitle', defaultMessage: 'Additional options' },
    optional: { id: 'translation.translationAdditionalOptionsPage.optional', defaultMessage: 'optional' },
    pageSubtitle: {
        id: 'translation.translationAdditionalOptionsPage.pageSubtitle',
        defaultMessage:
            'Make your translation job more specific to your use case by using your Custom Translator model or adding a glossary file'
    },
    modelIdSectionTitle: {
        id: 'translation.translationAdditionalOptionsPage.modelIdSectionTitle',
        defaultMessage: 'Category ID (Custom Translator model ID)'
    },
    modelIdSectionSubTitle1: {
        id: 'translation.translationAdditionalOptionsPage.modelIdSectionSubTitle1',
        defaultMessage: 'If you have a'
    },
    customTranslatorLink: {
        id: 'translation.translationAdditionalOptionsPage.customTranslatorLink',
        defaultMessage: 'Custom Translator'
    },
    modelIdSectionSubTitle2: {
        id: 'translation.translationAdditionalOptionsPage.modelIdSectionSubTitle2',
        defaultMessage:
            'model you can use it to help with your domain specific translation jobs. Enter the category ID of the Custom Translator model you wish to use.'
    },
    findCategoryIdLink: {
        id: 'translation.translationAdditionalOptionsPage.findCategoryIdLink',
        defaultMessage: 'Learn how to find the category ID.'
    },
    modelIdDropdownPlaceholder: {
        id: 'translation.translationAdditionalOptionsPage.modelIdDropdownPlaceholder',
        defaultMessage: 'Enter category ID'
    },
    glossaryFileSectionTitle: {
        id: 'translation.translationAdditionalOptionsPage.glossaryFileSectionTitle',
        defaultMessage: 'Add a glossary file (optional)'
    },
    glossaryFileSectionSubTitle: {
        id: 'translation.translationAdditionalOptionsPage.glossaryFileSectionSubTitle',
        defaultMessage:
            'A glossary file contains pairs of translated words or phrases. This is best used when you have specific translation requirements such as a product name or proper name that will help in translating your documents.'
    },
    learnAboutGlossaryFilesLink: {
        id: 'translation.translationAdditionalOptionsPage.learnAboutGlossaryFilesLink',
        defaultMessage: 'Learn more about glossary files.'
    },
    addGlossaryFileBtnTitle: {
        id: 'translation.translationAdditionalOptionsPage.addGlossaryFileBtnTitle',
        defaultMessage: 'Add a glossary file'
    },
    removeGlossaryFileBtnTitle: {
        id: 'translation.translationAdditionalOptionsPage.removeGlossaryFileBtnTitle',
        defaultMessage: 'Remove file'
    },
    languageTitle: {
        id: 'translation.translationAdditionalOptionsPage.languageTitle',
        defaultMessage: 'Select a language to translate from'
    },
    glossaryNameTitle: {
        id: 'translation.translationAdditionalOptionsPage.glossaryNameTitle',
        defaultMessage: 'Glossary file name'
    },
    emptyGlossaryFilesTablePlaceholder: {
        id: 'translation.translationAdditionalOptionsPage.emptyGlossaryFilesTablePlaceholder',
        defaultMessage: 'Upload a glossary file'
    },
    selectLocalFileSubMenu: {
        id: 'translation.translationAdditionalOptionsPage.selectLocalFileSubMenu',
        defaultMessage: 'From a local file'
    },
    selectStorageAccountSubMenu: {
        id: 'translation.translationAdditionalOptionsPage.selectStorageAccountSubMenu',
        defaultMessage: 'From a storage account'
    }
});

const ScrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { height: '100%', position: 'relative' }
});

const containerStyles = classNamesFunction<IStackProps, IStackItemStyles>()({
    root: { flex: 1, paddingLeft: '32px', paddingRight: '32px', overflow: 'auto', marginBottom: '10px' }
});

export const TranslationAdditionalOptionsPage = observer((props: TranslationAdditionalOptionsPageProps) => {
    const {
        onSubmit,
        onDismiss,
        onNext,
        onPrevious,
        translationConfigsDto,
        activeStep,
        isReturningUser,
        storageAccounts,
        selectedStorageAccount,
        resourceGroupName,
        selectedTargetLanguages,
        createdStorageContainerName,
        supportedLanguages
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { uploadLocalFiles } = useTranslatorCapabilities();

    const inputRef = React.useRef<HTMLInputElement>(null);
    const [customTranslatorModelId, setCustomTranslatorModelId] = React.useState<string>(translationConfigsDto.customTranslatorModelId);
    const [glossaryFiles, setGlossaryFiles] = React.useState<TargetFile[]>(translationConfigsDto.glossaryFiles);

    const { selection, selectedItems } = useSelection<TargetFile>();
    const [isUploading, setIsUploading] = React.useState<boolean>(false);

    const [selectedLanguageCode, setSelectedLanguageCode] = React.useState<string>(null);
    const [isAzureContainerBrowsingHidden, setIsAzureContainerBrowsingHidden] = React.useState<boolean>(true);

    const getUploadingFolderName = () => {
        const timeStamp = new Date().toISOString();
        const userAlias = rootStore.userStore.email.split('@')[0];

        return `glossaries/${userAlias}/${timeStamp}`;
    };

    const handleLocalFilesSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsUploading(true);
        const { files: filesList } = event.target as HTMLInputElement;
        const files = [...Array.from(filesList)];
        const folderName = getUploadingFolderName();
        await uploadLocalFiles({
            files,
            folderName,
            resourceGroupName,
            storageAccountName: selectedStorageAccount,
            containerName: createdStorageContainerName,
            onComplete: containerName => {
                setGlossaryFiles([
                    ...glossaryFiles,
                    ...files.map(f => ({
                        name: f.name,
                        languageCode: selectedLanguageCode,
                        containerName: `${containerName}/${folderName}`,
                        language: supportedLanguages.find(l => l.code === selectedLanguageCode).name
                    }))
                ]);
                setIsUploading(false);
            }
        });
    };

    const handleLocalStorageClick = (languageCode: string) => {
        setSelectedLanguageCode(languageCode);
        inputRef.current?.click();
    };

    const handleBlobStorageClick = (languageCode: string) => {
        setSelectedLanguageCode(languageCode);
        setIsAzureContainerBrowsingHidden(false);
    };

    const removeSelectedGlossaryFile = () => {
        const filteredUploadedFiles = glossaryFiles.filter(f => selectedItems.every(removedFile => removedFile.name !== f.name));
        setGlossaryFiles(filteredUploadedFiles);
        selection.setAllSelected(false);
        logDTButtonClick('document translation', 'jobCreation', 'Button', 'RemoveGlossaryFile', 'additionalOptions');
    };

    const getUniqueFiles = (prevFiles: TargetFile[], newFiles: TargetFile[]) =>
        [...prevFiles, ...newFiles].reduce((acc, val) => {
            if (!acc.some(item => item.name === val.name)) {
                acc.push(val);
            }
            return acc;
        }, []);

    const submitSelectedGlossaryFiles = (files: AzureBlobStore[], selectedContainer: string) => {
        setGlossaryFiles(
            getUniqueFiles(
                glossaryFiles,
                files.map(f => ({
                    name: f.name,
                    languageCode: selectedLanguageCode,
                    containerName: selectedContainer,
                    language: supportedLanguages.find(l => l.code === selectedLanguageCode).name
                }))
            )
        );
    };

    const addGlossaryFilesMenuItems: IContextualMenuItem[] = React.useMemo(
        () =>
            selectedTargetLanguages.map(language => ({
                key: language.key,
                role: 'menuitem',
                text: language.value,
                ariaLabel: language.value,
                subMenuProps: {
                    id: language.value,
                    ariaLabel: language.value,
                    items: [
                        {
                            key: 'localStorage',
                            role: 'menuitem',
                            name: localize(messages.selectLocalFileSubMenu),
                            onClick: () => handleLocalStorageClick(language.key)
                        },
                        {
                            key: 'storageAccount',
                            role: 'menuitem',
                            name: localize(messages.selectStorageAccountSubMenu),
                            onClick: () => handleBlobStorageClick(language.key)
                        }
                    ]
                }
            })),
        [selectedTargetLanguages]
    );

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'addFiles',
            iconProps: { iconName: 'add' },
            text: localize(messages.addGlossaryFileBtnTitle),
            onRender: () => (
                <ActionButton
                    onClick={() =>
                        logDTButtonClick('document translation', 'jobCreation', 'Button', 'AddGlossaryFile', 'additionalOptions')
                    }
                    text={localize(messages.addGlossaryFileBtnTitle)}
                    iconProps={{ iconName: 'add' }}
                    style={{ padding: '8px', height: '100%' }}
                    menuIconProps={{ style: { display: 'none' } }}
                    menuProps={{
                        id: 'addGlossaryFilesMenuItems',
                        items: addGlossaryFilesMenuItems,
                        ariaLabel: localize(messages.addGlossaryFileBtnTitle)
                    }}
                />
            )
        },
        {
            key: 'removeFile',
            iconProps: { iconName: 'delete' },
            text: localize(messages.removeGlossaryFileBtnTitle),
            disabled: !selectedItems.length || !selection,
            onClick: removeSelectedGlossaryFile
        }
    ];

    const filesTableColumn: IColumn[] = [
        {
            minWidth: 200,
            key: 'language',
            fieldName: 'language',
            isResizable: true,
            name: localize(messages.languageTitle)
        },
        {
            minWidth: 300,
            isResizable: true,
            key: 'name',
            fieldName: 'name',
            name: localize(messages.glossaryNameTitle),
            onRender: (item: File) => (
                <Stack horizontal verticalAlign="center" title={item.name} tokens={{ childrenGap: 20, maxWidth: '100%' }}>
                    <Icon iconName="TextDocument" />
                    <LuisText styles={{ root: { overflow: 'hidden' } }}>{item.name}</LuisText>
                </Stack>
            )
        }
    ];

    return (
        <Stack verticalFill verticalAlign="space-between" styles={{ root: { overflow: 'auto' } }}>
            <Stack styles={containerStyles} tokens={{ childrenGap: 13 }}>
                <Stack tokens={{ childrenGap: 13 }}>
                    <LuisText variant="xLarge" fontWeight={600}>
                        {localize(messages.pageTitle)}
                    </LuisText>
                    <LuisText variant="medium">{localize(messages.pageSubtitle)}</LuisText>
                </Stack>
                <Stack grow tokens={{ childrenGap: 20 }}>
                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.modelIdSectionTitle)}&nbsp;
                            <LuisText variant="medium" styles={{ root: { fontStyle: 'italic' } }}>
                                {localize(messages.optional)}
                            </LuisText>
                        </LuisText>
                        <LuisText>
                            {localize(messages.modelIdSectionSubTitle1)}
                            &nbsp;
                            <ExternalLink
                                isUnderlined={false}
                                onClick={() =>
                                    logDTLinkClick('document translation', 'jobCreation', 'customTranslation', 'additionalOptions')
                                }
                                href="https://portal.customtranslator.azure.ai/">
                                {localize(messages.customTranslatorLink)}
                            </ExternalLink>
                            &nbsp;
                            {localize(messages.modelIdSectionSubTitle2)}
                            &nbsp;
                            <ExternalLink
                                isUnderlined={false}
                                href="https://learn.microsoft.com/en-us/azure/cognitive-services/translator/custom-translator/how-to/train-custom-model#model-details">
                                {localize(messages.findCategoryIdLink)}
                            </ExternalLink>
                        </LuisText>
                        <LuisTextField
                            styles={{ root: { width: 300 } }}
                            value={customTranslatorModelId}
                            placeholder={localize(messages.modelIdDropdownPlaceholder)}
                            onChange={(_, modelId) => setCustomTranslatorModelId(modelId)}
                        />
                    </Stack>

                    <Stack tokens={{ childrenGap: 12 }}>
                        <LuisText variant="mediumPlus" fontWeight={600}>
                            {localize(messages.glossaryFileSectionTitle)}
                        </LuisText>
                        <LuisText>
                            {localize(messages.glossaryFileSectionSubTitle)}
                            &nbsp;
                            <ExternalLink
                                isUnderlined={false}
                                onClick={() =>
                                    logDTLinkClick('document translation', 'jobCreation', 'learnMoreGlossary', 'additionalOptions')
                                }
                                href="https://learn.microsoft.com/en-us/azure/ai-services/translator/document-translation/how-to-guides/create-use-glossaries">
                                {localize(messages.learnAboutGlossaryFilesLink)}
                            </ExternalLink>
                        </LuisText>
                    </Stack>

                    <Stack grow tokens={{ childrenGap: 10 }}>
                        <LuisCommandBar items={commandBarItems} />

                        <ScrollablePane styles={mergeStyleSets(ScrollablePaneStyles, { root: { minHeight: '250px' } })}>
                            <Table<TargetFile>
                                items={glossaryFiles}
                                selection={selection}
                                columns={filesTableColumn}
                                emptyPlaceholder={localize(messages.emptyGlossaryFilesTablePlaceholder)}
                            />
                        </ScrollablePane>
                    </Stack>
                </Stack>
            </Stack>
            <TranslationToolFooter
                activeStep={activeStep}
                isReturningUser={isReturningUser}
                onDismiss={onDismiss}
                onNext={() => onNext(customTranslatorModelId, glossaryFiles)}
                onPrevious={onPrevious}
                onSubmit={onSubmit}
                isLoading={isUploading}
            />
            <input multiple type="file" ref={inputRef} style={{ display: 'none' }} onChange={handleLocalFilesSelection} />
            <AzureContainerBrowsingModal
                storageAccounts={storageAccounts}
                selectedStorageAccount={selectedStorageAccount}
                onStorageAccountChange={null}
                isStorageAccountsLoading={false}
                useRemoteContainer={false}
                showSelectAllCheckBox={false}
                hidden={isAzureContainerBrowsingHidden}
                onSubmit={({ files, selectedContainer }) => {
                    submitSelectedGlossaryFiles(files, selectedContainer);
                    setIsAzureContainerBrowsingHidden(true);
                }}
                onDismiss={() => {
                    setIsAzureContainerBrowsingHidden(true);
                }}
            />
        </Stack>
    );
});
